import {
    PLAN_DISPLAY_ORDER,
    FEATURE_SECTION_LABELS,
    FEATURE_TEXT_OVERRIDES,
    MESSAGE_LINK_RE,
    BOT_ROOT_SELECTOR,
    ROUTE_CONTEXTS,
    ROUTE_TAB_CONTEXT_GROUPS,
    ROUTE_ENRICHMENTS,
} from "./constants";
export function parseReplyContent(response) {
    return (
        response?.choices?.[0]?.message?.content?.trim() ||
        "I can help you find pages, features, and next steps in Plany."
    );
}

export function toShortText(value, max = 140) {
    const text = String(value || "").replace(/\s+/g, " ").trim();
    if (!text) return "";
    return text.length > max ? `${text.slice(0, max - 3)}...` : text;
}

const FUZZY_NAVIGATION_VOCAB = [
    "go",
    "to",
    "open",
    "navigate",
    "switch",
    "business",
    "staff",
    "resident",
    "residents",
    "documents",
    "document",
    "docs",
    "doc",
    "vitals",
    "calendar",
    "schedule",
    "pricing",
    "subscription",
    "billing",
    "booking",
    "bookings",
    "hours",
    "hour",
    "profile",
    "integration",
    "image",
    "images",
];

const FUZZY_EXPIRED_DOCS_VOCAB = [
    "expired",
    "expire",
    "expiry",
    "documents",
    "document",
    "docs",
    "doc",
    "across",
    "everywhere",
    "check",
    "list",
];

const FUZZY_APPOINTMENTS_VOCAB = [
    "today",
    "todays",
    "appointment",
    "appointments",
    "booking",
    "bookings",
    "schedule",
    "coming",
    "upcoming",
    "next",
    "within",
    "day",
    "days",
    "my",
    "show",
    "what",
];

const FUZZY_BIRTHDAY_VOCAB = [
    "today",
    "todays",
    "birthday",
    "birthdays",
    "born",
    "coming",
    "upcoming",
    "next",
    "soon",
    "within",
    "day",
    "days",
    "staff",
    "resident",
    "residents",
    "show",
    "list",
];

const FUZZY_BOOKING_CREATE_VOCAB = [
    "create",
    "add",
    "make",
    "setup",
    "set",
    "up",
    "simple",
    "booking",
    "type",
    "service",
    "appointment",
];

function damerauLevenshteinDistance(left = "", right = "") {
    if (left === right) return 0;
    const a = String(left);
    const b = String(right);
    const rows = a.length + 1;
    const cols = b.length + 1;
    const matrix = Array.from({ length: rows }, () => new Array(cols).fill(0));

    for (let i = 0; i < rows; i += 1) {
        matrix[i][0] = i;
    }
    for (let j = 0; j < cols; j += 1) {
        matrix[0][j] = j;
    }

    for (let i = 1; i < rows; i += 1) {
        for (let j = 1; j < cols; j += 1) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            let best = Math.min(
                matrix[i - 1][j] + 1,
                matrix[i][j - 1] + 1,
                matrix[i - 1][j - 1] + cost
            );

            if (
                i > 1 &&
                j > 1 &&
                a[i - 1] === b[j - 2] &&
                a[i - 2] === b[j - 1]
            ) {
                best = Math.min(best, matrix[i - 2][j - 2] + cost);
            }
            matrix[i][j] = best;
        }
    }

    return matrix[rows - 1][cols - 1];
}

function getFuzzyThreshold(wordLength) {
    if (wordLength <= 4) return 1;
    if (wordLength <= 8) return 2;
    return 3;
}

function fuzzyNormalizeToken(token, vocabulary = []) {
    const value = String(token || "").toLowerCase();
    if (!value || value.length < 3 || !Array.isArray(vocabulary) || !vocabulary.length) {
        return value;
    }

    let bestWord = value;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const candidate of vocabulary) {
        const target = String(candidate || "").toLowerCase();
        if (!target) continue;
        const lengthDelta = Math.abs(target.length - value.length);
        const maxDelta = getFuzzyThreshold(value.length);
        if (lengthDelta > maxDelta) continue;

        const distance = damerauLevenshteinDistance(value, target);
        if (distance < bestDistance) {
            bestDistance = distance;
            bestWord = target;
            if (distance === 0) break;
        }
    }

    if (!Number.isFinite(bestDistance)) return value;
    return bestDistance <= getFuzzyThreshold(value.length) ? bestWord : value;
}

function fuzzyNormalizeText(text = "", vocabulary = []) {
    return String(text || "")
        .toLowerCase()
        .replace(/[a-z]+/g, (token) => fuzzyNormalizeToken(token, vocabulary));
}

export function isPlanPricingQuestion(text) {
    const lower = String(text || "").toLowerCase();
    return /price|pricing|cost|how much|per month|monthly|standard.*premium|premium.*standard/.test(lower);
}

export function isPlanFeaturesQuestion(text) {
    const lower = String(text || "").toLowerCase();
    return /feature|features|includes|included|what.*plan|plan.*include|difference.*plan|compare.*plan/.test(lower);
}

export function isPlanSwitchSeatsQuestion(text) {
    const lower = String(text || "").toLowerCase();
    return /switch.*plan|change.*plan|upgrade|downgrade|adjust.*seat|resident.*seat|seat.*resident|resident.*limit|update.*limit/.test(lower);
}

export function isTodaysAppointmentsQuestion(text) {
    const lower = fuzzyNormalizeText(text, FUZZY_APPOINTMENTS_VOCAB);
    const hasAppointmentTerm = /(appointment|appointments|booking|bookings|schedule)/.test(lower);
    if (!hasAppointmentTerm) return false;
    return (
        /(today|todays)/.test(lower) ||
        /(coming|upcoming|next|within).*\bday(s)?\b/.test(lower) ||
        /\b\d{1,3}\s*day(s)?\b/.test(lower) ||
        /(show|list|what|my).*(appointment|appointments|booking|bookings|schedule)/.test(lower)
    );
}

export function isTodaysBirthdayQuestion(text) {
    const lower = fuzzyNormalizeText(text, FUZZY_BIRTHDAY_VOCAB);
    const hasBirthdayTerm = /(birthday|birthdays|born)/.test(lower);
    if (!hasBirthdayTerm) return false;
    return (
        /(today|todays)/.test(lower) ||
        /\bsoon\b/.test(lower) ||
        /(coming|upcoming|next|within).*\bday(s)?\b/.test(lower) ||
        /\b\d{1,3}\s*day(s)?\b/.test(lower) ||
        /(show|list|who|any|is there).*(birthday|birthdays|born)/.test(lower)
    );
}

export function isCreateBookingTypeIntent(text) {
    const normalized = fuzzyNormalizeText(text, FUZZY_BOOKING_CREATE_VOCAB);
    const hasCreateVerb = /\b(create|add|make|setup)\b/.test(normalized);
    const hasBookingType = /\bbooking\b.*\btype\b|\bappointment\b.*\btype\b|\bservice\b.*\btype\b/.test(normalized);
    return hasCreateVerb && hasBookingType;
}

export function isConfirmCreateBookingType(text) {
    const lower = String(text || "").toLowerCase();
    return /^\s*1\s*$/.test(lower) || /confirm.*create.*booking type|yes.*create.*booking type|create it now/.test(lower);
}

export function isBookForMeQuestion(text) {
    const lower = String(text || "").toLowerCase();
    return /(book|schedule|create|make).*(appointment|appoint|appt|app|booking)/.test(lower);
}

export function buildBookingTypeSlug(title = "quick-booking") {
    const base = String(title)
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .slice(0, 42);
    const suffix = String(Date.now()).slice(-6);
    return `${base || "quick-booking"}-${suffix}`;
}

export function parseDurationMinutes(text, defaultMinutes = 30) {
    const lower = String(text || "").toLowerCase();
    const hourMatch = lower.match(/(\d+)\s*(hour|hours|hr|hrs|h)\b/);
    if (hourMatch) {
        const hours = Number(hourMatch[1]);
        if (Number.isFinite(hours) && hours > 0) return hours * 60;
    }
    const minuteMatch = lower.match(/(\d+)\s*(minute|minutes|min|mins|mn|m)\b/);
    if (minuteMatch) {
        const mins = Number(minuteMatch[1]);
        if (Number.isFinite(mins) && mins > 0) return mins;
    }
    return defaultMinutes;
}

export function formatBookingTypeCategoryLabel(categoryId) {
    return Number(categoryId) === 1 ? "Event" : Number(categoryId) === 3 ? "Class" : "Service";
}

export function buildBookingTypeModePrompt() {
    return [
        "Choose how to create booking type:",
        "1. Quick default (30 min, all days, 9:00 AM to 5:00 PM, no buffer)",
        "2. Guided setup (title, duration, days, times, buffer)",
        "Reply with `1` or `2`.",
    ].join("\n");
}

export function buildBookingTypeReadyReply(draft = {}, options = {}) {
    const quick = Boolean(options?.quick);
    return [
        quick ? "Quick booking type draft ready:" : "Booking type draft ready:",
        `- Title: ${draft?.title || ""}`,
        `- Duration: ${Number(draft?.durationMinutes || 0)} minutes`,
        `- Category: ${formatBookingTypeCategoryLabel(draft?.bookingTypeCategoryId)}`,
        `- Days: ${Array.isArray(draft?.availabilityDays) ? draft.availabilityDays.join(", ") : ""}`,
        `- Time: ${draft?.availabilityStartLabel || ""} to ${draft?.availabilityEndLabel || ""}`,
        `- Buffer: ${Number(draft?.bufferMinutes || 0)} minutes`,
        "Reply `1` to confirm create, or `2` to cancel.",
    ].join("\n");
}

export function buildInviteBookingTypeChoiceReply(recipient = "", candidates = []) {
    const safeRecipient = String(recipient || "").trim();
    const list = Array.isArray(candidates) ? candidates.slice(0, 8) : [];
    return [
        `I found multiple booking types. Which one should I send to ${safeRecipient}?`,
        ...list.map((item, index) => `${index + 1}. ${item.title} (${item.durationMinutes || 30} min)`),
        "Reply with number, or `2` to cancel.",
    ].join("\n");
}

export function parseSimpleBookingTypeDraft(text) {
    const raw = String(text || "");
    const titleMatch =
        raw.match(/(?:called|named|title)\s*:?\s*["']?([^"'\n]+)["']?/i) ||
        raw.match(/booking type\s+["']?([^"'\n]+)["']?/i);
    const title = toShortText(
        (titleMatch?.[1] || "Quick Booking Type").replace(/\s+/g, " ").trim(),
        72
    );
    const durationMinutes = parseDurationMinutes(raw);
    const lower = raw.toLowerCase();
    let bookingTypeCategoryId = 2; // service
    if (/\bevent\b/.test(lower)) bookingTypeCategoryId = 1;
    if (/\bclass\b/.test(lower)) bookingTypeCategoryId = 3;
    const genericDesc = `${title} booking. Please select a suitable time slot.`;
    return {
        title,
        durationMinutes,
        bookingTypeCategoryId,
        meetingLocationTypeId: "4",
        location: "No location",
        desc: genericDesc,
        color: "#2563eb",
        availabilityNotes: "",
        bufferMinutes: null,
    };
}

export function isBookingTypeDraftRefinement(text) {
    const lower = String(text || "").toLowerCase();
    return /(monday|mon|tuesday|tue|wednesday|wed|thursday|thu|friday|fri|saturday|sat|sunday|sun|availability|available|buffer|after|from|until|till|midnight)/.test(lower);
}

export function applyBookingTypeDraftRefinement(draft = {}, text = "") {
    const next = { ...(draft || {}) };
    const raw = String(text || "").trim();
    const lower = raw.toLowerCase();
    let changed = false;

    const titleMatch = raw.match(/(?:title)\s*:?\s*["']?([^"'\n]+)["']?/i);
    if (titleMatch?.[1]) {
        const nextTitle = toShortText(titleMatch[1].replace(/\s+/g, " ").trim(), 72);
        if (nextTitle && nextTitle !== next.title) {
            next.title = nextTitle;
            changed = true;
        }
    }

    const bufferMatch =
        lower.match(/\bbuffer(?:\s*(?:of|is|:))?\s*(\d{1,3})\s*(?:min|mins|minute|minutes)\b/i) ||
        lower.match(/\b(\d{1,3})\s*(?:min|mins|minute|minutes)\s*buffer\b/i);
    if (bufferMatch?.[1]) {
        const parsed = Number(bufferMatch[1]);
        if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 240) {
            next.bufferMinutes = parsed;
            changed = true;
        }
    }

    if (isBookingTypeDraftRefinement(lower)) {
        const existing = String(next.availabilityNotes || "").trim();
        const appended = raw;
        if (!existing) {
            next.availabilityNotes = appended;
            changed = true;
        } else if (!existing.toLowerCase().includes(appended.toLowerCase())) {
            next.availabilityNotes = `${existing}\n${appended}`;
            changed = true;
        }
    }

    return { draft: next, changed };
}

export function normalizePlanFeatures(features) {
    if (!features) return {};
    if (typeof features === "object") return features;
    try {
        return JSON.parse(features);
    } catch (_error) {
        return {};
    }
}

export function detectRequestedPlanCodes(text) {
    const lower = String(text || "").toLowerCase();
    const codes = [];
    if (/\bfree\b/.test(lower)) codes.push("FREE");
    if (/\bstandard\b/.test(lower)) codes.push("STANDARD");
    if (/\bpremium\b/.test(lower)) codes.push("PREMIUM");
    if (/\benterprise\b/.test(lower)) codes.push("ENTERPRISE");
    return [...new Set(codes)];
}

export function filterPlansByRequestedCodes(plans = [], requestedCodes = []) {
    if (!requestedCodes.length) return plans;
    const wanted = new Set(requestedCodes.map((code) => String(code).toUpperCase()));
    const filtered = plans.filter((plan) => wanted.has(String(plan?.code || "").toUpperCase()));
    return filtered.length ? filtered : plans;
}

export function getPlanDisplayName(plan) {
    return plan?.name || `${plan?.code || "Plan"}`;
}

export function getPlanPricing(plan) {
    const base = Number(plan?.pricing?.baseMonthly);
    const perUnit = Number(
        plan?.pricing?.perUnitMonthly ??
        plan?.pricing?.perResidentMonthly ??
        plan?.pricing?.perStaffMonthly
    );
    return {
        baseMonthly: Number.isFinite(base) ? base : null,
        perUnitMonthly: Number.isFinite(perUnit) ? perUnit : null,
        quantityMetric: String(
            plan?.pricing?.quantityMetric ||
            (plan?.pricing?.perStaffMonthly != null ? "staff" : "resident")
        ).toLowerCase(),
    };
}

export function orderPlansForDisplay(plans = []) {
    const ranked = [...plans];
    ranked.sort((left, right) => {
        const leftRank = PLAN_DISPLAY_ORDER.indexOf(String(left?.code || "").toUpperCase());
        const rightRank = PLAN_DISPLAY_ORDER.indexOf(String(right?.code || "").toUpperCase());
        const l = leftRank === -1 ? Number.MAX_SAFE_INTEGER : leftRank;
        const r = rightRank === -1 ? Number.MAX_SAFE_INTEGER : rightRank;
        if (l !== r) return l - r;
        return String(getPlanDisplayName(left)).localeCompare(String(getPlanDisplayName(right)));
    });
    return ranked;
}

export function formatFeatureSectionLabel(key) {
    if (!key) return "";
    return FEATURE_SECTION_LABELS[key] || toShortText(key, 60);
}

export function formatFeatureItem(text) {
    const raw = String(text || "").trim();
    if (!raw) return "";
    return FEATURE_TEXT_OVERRIDES[raw] || raw;
}

export function buildPlanPricingReply(plans = [], subscription = null, requestedCodes = []) {
    if (!plans.length) {
        return "I cannot read live plan pricing right now. Open /biz/pricing to view the backend pricing catalog.";
    }

    const orderedPlans = orderPlansForDisplay(
        filterPlansByRequestedCodes(plans, requestedCodes)
    );
    const currentPlan = orderedPlans.find(
        (plan) => String(plan?.id) === String(subscription?.planId)
    );
    const lines = ["Current plan pricing:"];

    orderedPlans.forEach((plan) => {
        const { baseMonthly, perUnitMonthly, quantityMetric } = getPlanPricing(plan);
        const planName = getPlanDisplayName(plan);
        const isFree = String(plan?.code || "").toUpperCase() === "FREE";
        if (isFree) {
            lines.push(`- ${planName}: Free`);
            return;
        }

        if (baseMonthly == null && perUnitMonthly == null) {
            lines.push(`- ${planName}: Pricing is configured in the backend catalog, but no monthly amount is available in this response.`);
            return;
        }

        const parts = [];
        if (baseMonthly != null) {
            parts.push(`$${baseMonthly} per business / month`);
        }
        if (perUnitMonthly != null) {
            parts.push(`$${perUnitMonthly} per ${quantityMetric === "staff" ? "staff" : "resident"} / month`);
        }
        lines.push(`- ${planName}: ${parts.join(" + ")}`);
    });

    if (currentPlan) {
        lines.push(`Your current plan: ${getPlanDisplayName(currentPlan)}.`);
    }

    lines.push("Billing note: billed monthly, tax not included.");
    return lines.join("\n");
}

export function buildPlanFeaturesReply(plans = [], requestedCodes = []) {
    if (!plans.length) {
        return "I cannot read plan features right now. Open /biz/pricing to view configured features by plan.";
    }

    const orderedPlans = orderPlansForDisplay(
        filterPlansByRequestedCodes(plans, requestedCodes)
    );
    const lines = ["Plan features summary:"];

    orderedPlans.forEach((plan) => {
        const features = normalizePlanFeatures(plan?.features);
        const sections = Object.entries(features || {}).filter(([, items]) => Array.isArray(items) && items.length);
        lines.push(`${getPlanDisplayName(plan)}:`);

        if (!sections.length) {
            lines.push("- Feature list is not configured.");
            return;
        }

        sections.slice(0, 5).forEach(([sectionKey, items]) => {
            const label = formatFeatureSectionLabel(sectionKey);
            const summaryItems = (items || [])
                .map((item) => formatFeatureItem(item))
                .filter(Boolean)
                .slice(0, 4);
            if (!summaryItems.length) return;
            lines.push(`- ${label}: ${summaryItems.join(", ")}`);
        });
    });

    return lines.join("\n");
}

export function buildSwitchPlanAndSeatsReply(runtimeContext, subscription = null) {
    const isOnPricingPage = /^\/biz\/pricing(?:\/|$)/.test(runtimeContext?.pathname || "");
    const currentPlanId = subscription?.planId ? ` (current plan id: ${subscription.planId})` : "";
    const intro = isOnPricingPage
        ? "You can do this directly on this Pricing page."
        : "Go to /biz/pricing to switch plan and manage resident seats.";

    return [
        intro,
        `1. In Resident seats, adjust + or - to set desired quantity${currentPlanId}.`,
        "2. If you are changing seats on an active subscription, click Update Resident Limit.",
        "3. In the target plan card (Standard or Premium), click Subscribe.",
        "4. Complete Stripe checkout to confirm plan change.",
        "5. After return, confirm the card shows Current Plan and seats summary is updated.",
    ].join("\n");
}

export function buildPlanIntentReply(userMessage, runtimeContext, plans = [], subscription = null) {
    const askPricing = isPlanPricingQuestion(userMessage);
    const askFeatures = isPlanFeaturesQuestion(userMessage);
    const askSwitchSeats = isPlanSwitchSeatsQuestion(userMessage);
    if (!askPricing && !askFeatures && !askSwitchSeats) return "";
    const requestedCodes = detectRequestedPlanCodes(userMessage);

    const blocks = [];
    if (askPricing) blocks.push(buildPlanPricingReply(plans, subscription, requestedCodes));
    if (askFeatures) blocks.push(buildPlanFeaturesReply(plans, requestedCodes));
    if (askSwitchSeats) blocks.push(buildSwitchPlanAndSeatsReply(runtimeContext, subscription));
    return blocks.filter(Boolean).join("\n\n");
}

function parseAppointmentsWindowDays(text = "") {
    const lower = fuzzyNormalizeText(text, FUZZY_APPOINTMENTS_VOCAB);
    const hasWindowLanguage =
        /(coming|upcoming|within|next)\b/.test(lower) && /\bday(s)?\b/.test(lower);
    const hasPlaceholder = /\bx\s*day(s)?\b/.test(lower);
    const explicitMatch =
        lower.match(/(?:within|next|coming|upcoming|in)\s*(\d{1,3})\s*day(s)?\b/) ||
        lower.match(/(\d{1,3})\s*day(s)?\b(?:\s*(?:from now|ahead|out))?/);

    if (explicitMatch?.[1]) {
        const parsedDays = Number(explicitMatch[1]);
        if (Number.isFinite(parsedDays)) {
            return {
                requested: true,
                days: Math.max(0, Math.min(parsedDays, 365)),
            };
        }
    }

    return {
        requested: hasWindowLanguage || hasPlaceholder,
        days: null,
    };
}

function filterAppointmentsInWindow(bookings = [], providerUser = {}, windowDays = null) {
    const now = new Date();
    const today = startOfDay(now);
    const windowEnd = Number.isFinite(windowDays)
        ? startOfDay(new Date(today.getTime() + (windowDays * 24 * 60 * 60 * 1000)))
        : null;

    return (Array.isArray(bookings) ? bookings : [])
        .filter((booking) => {
            const when = new Date(booking?.time);
            if (Number.isNaN(when.getTime())) return false;
            const whenDay = startOfDay(when);
            const status = String(booking?.status || "").toUpperCase();
            if (["DELETED", "CANCELLED", "CANCELED"].includes(status)) return false;
            if (providerUser?.staffId && booking?.staffId != null) {
                if (Number(booking.staffId) !== Number(providerUser.staffId)) return false;
            }
            if (providerUser?.email && booking?.staffEmail) {
                if (String(booking.staffEmail).toLowerCase() !== String(providerUser.email).toLowerCase()) {
                    return false;
                }
            }
            if (Number.isFinite(windowDays)) {
                return whenDay >= today && whenDay <= windowEnd;
            }
            return (
                when.getFullYear() === now.getFullYear() &&
                when.getMonth() === now.getMonth() &&
                when.getDate() === now.getDate()
            );
        })
        .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
}

export function countTodaysAppointments(bookings = [], providerUser = {}) {
    return filterAppointmentsInWindow(bookings, providerUser, null).length;
}

export function buildTodaysAppointmentsReply(userMessage, bookings = [], providerUser = {}) {
    if (!isTodaysAppointmentsQuestion(userMessage)) return "";
    const appointmentWindow = parseAppointmentsWindowDays(userMessage);
    if (appointmentWindow.requested && appointmentWindow.days === null) {
        return "Tell me the number of days for appointments, for example: Show my appointments in next 7 days.";
    }
    const windowDays = appointmentWindow.days;
    const filtered = filterAppointmentsInWindow(bookings, providerUser, windowDays);

    if (!filtered.length) {
        return Number.isFinite(windowDays)
            ? `You have no appointments scheduled in the next ${windowDays} day(s).`
            : "You have no appointments scheduled for today.";
    }

    const lines = [
        Number.isFinite(windowDays)
            ? `You have ${filtered.length} appointment(s) in next ${windowDays} day(s) (including today):`
            : `You have ${filtered.length} appointment(s) today:`,
    ];
    filtered.slice(0, 8).forEach((booking) => {
        const when = new Date(booking.time);
        const dateLabel = when.toLocaleDateString([], { month: "2-digit", day: "2-digit" });
        const timeLabel = when.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        const title = booking?.bookingTypeName || "Appointment";
        const invitee = booking?.inviteeName ? ` with ${booking.inviteeName}` : "";
        lines.push(
            Number.isFinite(windowDays)
                ? `- ${dateLabel} ${timeLabel}: ${title}${invitee}`
                : `- ${timeLabel}: ${title}${invitee}`
        );
    });
    if (filtered.length > 8) {
        lines.push(`+ ${filtered.length - 8} more. Open /biz/calendar for the full list.`);
    }
    return lines.join("\n");
}

export function buildBookForMeReply(userMessage) {
    if (!isBookForMeQuestion(userMessage)) return "";
    return [
        "I can make this more agentic in the next step.",
        "For now I can already do:",
        "1. Show today's appointments",
        "2. Create a simple booking type draft",
        "To auto-book for you safely, I need these inputs first: booking type, date/time, invitee name, invitee email, and staff.",
        "If you want, I can implement a confirmed booking action next (with a final confirmation before submit).",
    ].join("\n");
}

export function isExpiredDocumentsQuestion(text) {
    const lower = fuzzyNormalizeText(text, FUZZY_EXPIRED_DOCS_VOCAB);
    return (
        /(expired|expire|expiry).*(document|documents|doc|docs)/.test(lower) ||
        /(document|documents|doc|docs).*(expired|expire|expiry)/.test(lower) ||
        /(expiring|upcoming|coming|next|within).*(\d{1,3}\s*(day|days)).*(document|documents|doc|docs)/.test(lower) ||
        /(document|documents|doc|docs).*(expiring|upcoming|coming|next|within).*(\d{1,3}\s*(day|days))/.test(lower) ||
        /(expiry|expired|expiring).*(next|within|coming|upcoming).*(\d{1,3}\s*(day|days))/.test(lower) ||
        /(next|within|coming|upcoming).*(\d{1,3}\s*(day|days)).*(expiry|expired|expiring)/.test(lower) ||
        /(expiry|expired|expiring).*(\d{1,3}\s*(day|days))/.test(lower) ||
        /check.*everywhere.*(document|doc)/.test(lower) ||
        /any.*expired.*across/.test(lower)
    );
}

function parseExpiresDateFromText(text = "") {
    const value = String(text || "");
    const match = value.match(/expires\s+([a-z]{3,9}\s+\d{1,2},\s+\d{4}|\d{1,2}\/\d{1,2}\/\d{2,4})/i);
    if (!match?.[1]) return null;
    const parsed = new Date(match[1]);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
}

function getNearestDocumentSectionLabel(element) {
    let cursor = element;
    for (let depth = 0; depth < 6 && cursor; depth += 1) {
        const heading = cursor.querySelector(
            "h4, h5, h6, .MuiTypography-h4, .MuiTypography-h5, .MuiTypography-h6, .MuiTypography-subtitle1"
        );
        const text = toShortText(heading?.textContent || "", 80);
        if (text) return text;
        cursor = cursor.parentElement;
    }
    return "";
}

export function collectVisibleExpiredDocuments(limit = 20) {
    if (typeof document === "undefined") return [];
    const rows = Array.from(document.querySelectorAll(".MuiListItem-root, li"))
        .filter((row) => isVisibleElement(row) && !isElementInsidePlanyBot(row));
    const now = new Date();
    const collected = [];
    const seen = new Set();

    for (const row of rows) {
        const fileLink = row.querySelector("a[href]");
        const fileName = toShortText(fileLink?.textContent || "", 140);
        if (!fileName) continue;

        const rowText = toShortText(row.textContent || "", 240);
        if (!/upload|expires?/i.test(rowText)) continue;

        const hasExpiredChip = Boolean(
            Array.from(row.querySelectorAll(".MuiChip-label"))
                .find((chip) => /expired/i.test(String(chip.textContent || "")))
        );
        const expiresDate = parseExpiresDateFromText(rowText);
        const isExpiredByDate = Boolean(expiresDate && expiresDate < now);
        if (!hasExpiredChip && !isExpiredByDate) continue;

        const section = getNearestDocumentSectionLabel(row);
        const key = `${fileName}::${section}`;
        if (seen.has(key)) continue;
        seen.add(key);

        collected.push({
            fileName,
            section,
            expiresAt: expiresDate ? expiresDate.toLocaleDateString() : "",
        });
        if (collected.length >= limit) break;
    }

    return collected;
}

export function buildExpiredDocumentsReply(userMessage, runtimeContext = {}) {
    if (!isExpiredDocumentsQuestion(userMessage)) return "";
    const expiredDocs = collectVisibleExpiredDocuments();
    if (!expiredDocs.length) {
        return [
            "I checked the visible document lists on this page and found no expired documents.",
            "I could not complete the cross-page lookup this time. Please ask again.",
        ].join("\n");
    }

    const lines = ["Expired documents I can see now:"];
    expiredDocs.slice(0, 12).forEach((doc) => {
        const detailParts = [];
        if (doc.section) detailParts.push(doc.section);
        if (doc.expiresAt) detailParts.push(`expires ${doc.expiresAt}`);
        const details = detailParts.length ? ` (${detailParts.join(" • ")})` : "";
        lines.push(`- ${doc.fileName}${details}`);
    });
    if (expiredDocs.length > 12) {
        lines.push(`+ ${expiredDocs.length - 12} more`);
    }
    if (/everywhere|across/i.test(String(userMessage || ""))) {
        const path = runtimeContext?.pathname ? ` Current page: ${runtimeContext.pathname}.` : "";
        lines.push(`Checked all visible document sections on this screen.${path}`);
    }
    return lines.join("\n");
}

function parseBirthdayParts(value) {
    if (!value) return null;

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return {
            year: value.getFullYear(),
            month: value.getMonth() + 1,
            day: value.getDate(),
            monthUtc: value.getUTCMonth() + 1,
            dayUtc: value.getUTCDate(),
        };
    }

    const raw = String(value || "").trim();
    if (!raw) return null;

    const isoLike = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoLike) {
        const year = Number(isoLike[1]);
        const month = Number(isoLike[2]);
        const day = Number(isoLike[3]);
        if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
            return { year, month, day, monthUtc: month, dayUtc: day };
        }
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return {
        year: parsed.getFullYear(),
        month: parsed.getMonth() + 1,
        day: parsed.getDate(),
        monthUtc: parsed.getUTCMonth() + 1,
        dayUtc: parsed.getUTCDate(),
    };
}

function getBirthdayPartsFromEntity(entity = {}) {
    const direct = [
        entity?.dateOfBirth,
        entity?.birthday,
        entity?.birthDate,
        entity?.dob,
        entity?.user?.dateOfBirth,
        entity?.user?.birthday,
        entity?.user?.birthDate,
        entity?.user?.dob,
    ];

    for (const candidate of direct) {
        const parts = parseBirthdayParts(candidate);
        if (parts) return parts;
    }
    return null;
}

function isBirthdayToday(parts, today = new Date()) {
    if (!parts) return false;
    const month = today.getMonth() + 1;
    const day = today.getDate();
    return (
        (parts.month === month && parts.day === day) ||
        (parts.monthUtc === month && parts.dayUtc === day)
    );
}

export function countTodaysBirthdays(residents = [], staffs = []) {
    const today = new Date();
    const residentCount = (Array.isArray(residents) ? residents : []).reduce((count, resident) => {
        const birthday = getBirthdayPartsFromEntity(resident);
        return birthday && isBirthdayToday(birthday, today) ? count + 1 : count;
    }, 0);
    const staffCount = (Array.isArray(staffs) ? staffs : []).reduce((count, staff) => {
        const birthday = getBirthdayPartsFromEntity(staff);
        return birthday && isBirthdayToday(birthday, today) ? count + 1 : count;
    }, 0);
    return residentCount + staffCount;
}

function parseBirthdayWindowDays(text = "") {
    const lower = fuzzyNormalizeText(text, FUZZY_BIRTHDAY_VOCAB);
    const hasWindowLanguage =
        ((/(coming|upcoming|within|next)\b/.test(lower) && /\bday(s)?\b/.test(lower)) ||
            /\bsoon\b/.test(lower));
    const hasPlaceholder = /\bx\s*day(s)?\b/.test(lower);
    const explicitMatch =
        lower.match(/(?:within|next|coming|upcoming|in)\s*(\d{1,3})\s*day(s)?\b/) ||
        lower.match(/(\d{1,3})\s*day(s)?\b(?:\s*(?:from now|ahead|out))?/);

    if (explicitMatch?.[1]) {
        const parsedDays = Number(explicitMatch[1]);
        if (Number.isFinite(parsedDays)) {
            return {
                requested: true,
                days: Math.max(0, Math.min(parsedDays, 365)),
            };
        }
    }

    if (hasWindowLanguage) {
        return {
            requested: true,
            days: 30,
        };
    }

    return {
        requested: hasWindowLanguage || hasPlaceholder,
        days: null,
    };
}

function startOfDay(value = new Date()) {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
}

function getDaysUntilNextBirthday(parts, today = new Date()) {
    if (!parts) return null;
    const base = startOfDay(today);

    const toCandidate = (month, day) => {
        if (!Number.isFinite(Number(month)) || !Number.isFinite(Number(day))) return null;
        const monthValue = Number(month);
        const dayValue = Number(day);
        if (monthValue < 1 || monthValue > 12 || dayValue < 1 || dayValue > 31) return null;
        const year = base.getFullYear();
        let candidate = new Date(year, monthValue - 1, dayValue);
        candidate = startOfDay(candidate);
        if (Number.isNaN(candidate.getTime())) return null;
        if (candidate < base) {
            candidate = new Date(year + 1, monthValue - 1, dayValue);
            candidate = startOfDay(candidate);
        }
        if (Number.isNaN(candidate.getTime())) return null;
        return candidate;
    };

    const candidates = [
        toCandidate(parts.month, parts.day),
        toCandidate(parts.monthUtc, parts.dayUtc),
    ].filter(Boolean);

    if (!candidates.length) return null;
    const next = candidates.reduce((best, current) => (current < best ? current : best), candidates[0]);
    return Math.round((next.getTime() - base.getTime()) / (24 * 60 * 60 * 1000));
}

export function buildTodaysBirthdayReply(userMessage, residents = [], staffs = []) {
    if (!isTodaysBirthdayQuestion(userMessage)) return "";

    const today = new Date();
    const birthdayWindow = parseBirthdayWindowDays(userMessage);
    if (birthdayWindow.requested && birthdayWindow.days === null) {
        return "Tell me the number of days for birthdays, for example: Show birthdays in next 30 days.";
    }
    const upcomingDays = birthdayWindow.days;
    const residentList = Array.isArray(residents) ? residents : [];
    const staffList = Array.isArray(staffs) ? staffs : [];

    const residentBirthdays = residentList
        .map((resident) => {
            const birthday = getBirthdayPartsFromEntity(resident);
            if (!birthday) return null;
            const daysUntil = getDaysUntilNextBirthday(birthday, today);
            if (!Number.isFinite(daysUntil)) return null;
            if (upcomingDays == null) {
                if (!isBirthdayToday(birthday, today)) return null;
            } else if (daysUntil < 0 || daysUntil > upcomingDays) {
                return null;
            }
            const name = [resident?.firstName, resident?.lastName]
                .map((part) => String(part || "").trim())
                .filter(Boolean)
                .join(" ");
            const age = Number.isFinite(birthday?.year)
                ? Math.max(0, (today.getFullYear() + (daysUntil === 0 ? 0 : 1)) - birthday.year)
                : null;
            return {
                label: name || `Resident #${resident?.id || "?"}`,
                age,
                type: "Resident",
                daysUntil,
                month: birthday.month || birthday.monthUtc,
                day: birthday.day || birthday.dayUtc,
            };
        })
        .filter(Boolean);

    const staffBirthdays = staffList
        .map((staff) => {
            const birthday = getBirthdayPartsFromEntity(staff);
            if (!birthday) return null;
            const daysUntil = getDaysUntilNextBirthday(birthday, today);
            if (!Number.isFinite(daysUntil)) return null;
            if (upcomingDays == null) {
                if (!isBirthdayToday(birthday, today)) return null;
            } else if (daysUntil < 0 || daysUntil > upcomingDays) {
                return null;
            }
            const name = String(
                staff?.name ||
                    staff?.user?.username ||
                    staff?.user?.email ||
                    ""
            ).trim();
            const age = Number.isFinite(birthday?.year)
                ? Math.max(0, (today.getFullYear() + (daysUntil === 0 ? 0 : 1)) - birthday.year)
                : null;
            return {
                label: name || `Staff #${staff?.id || "?"}`,
                age,
                type: "Staff",
                daysUntil,
                month: birthday.month || birthday.monthUtc,
                day: birthday.day || birthday.dayUtc,
            };
        })
        .filter(Boolean);

    const hasAnyStaffBirthDateData = staffList.some((staff) =>
        Boolean(getBirthdayPartsFromEntity(staff))
    );

    const allBirthdays = [...residentBirthdays, ...staffBirthdays];
    if (!allBirthdays.length) {
        return [
            upcomingDays == null
                ? "No resident or staff birthdays today."
                : `No resident or staff birthdays in the next ${upcomingDays} day(s).`,
            `Checked: ${residentList.length} resident profile(s), ${staffList.length} staff profile(s).`,
            !hasAnyStaffBirthDateData && staffList.length
                ? "Staff date-of-birth data is not available in current staff profiles."
                : "",
        ]
            .filter(Boolean)
            .join("\n");
    }

    const lines = [
        upcomingDays == null
            ? "Today's birthdays:"
            : `Upcoming birthdays in next ${upcomingDays} day(s):`,
    ];
    allBirthdays
        .sort(
            (a, b) =>
                (a.daysUntil ?? 0) - (b.daysUntil ?? 0) ||
                a.type.localeCompare(b.type) ||
                a.label.localeCompare(b.label)
        )
        .forEach((item) => {
            const dateLabel =
                Number.isFinite(item.month) && Number.isFinite(item.day)
                    ? ` on ${String(item.month).padStart(2, "0")}/${String(item.day).padStart(2, "0")}`
                    : "";
            const dueLabel =
                upcomingDays == null
                    ? ""
                    : item.daysUntil === 0
                        ? " (today)"
                        : ` (in ${item.daysUntil} day(s))`;
            lines.push(
                `- ${item.type}: ${item.label}${Number.isFinite(item.age) ? ` (turns ${item.age})` : ""}${dateLabel}${dueLabel}`
            );
        });

    lines.push(
        `Checked: ${residentList.length} resident profile(s), ${staffList.length} staff profile(s).`
    );
    if (!hasAnyStaffBirthDateData && staffList.length) {
        lines.push("Staff date-of-birth data is not available in current staff profiles.");
    }
    return lines.join("\n");
}

export function isCalendarNavigationQuestion(text) {
    const lower = String(text || "").toLowerCase();
    return /access.*calendar|open.*calendar|go to.*calendar|where.*calendar|view.*calendar/.test(lower);
}

export function buildCalendarNavigationReply(pathname = "") {
    const isBusinessArea = /^\/biz(?:\/|$)/.test(pathname);
    const primaryRoute = isBusinessArea ? "/biz/calendar" : "/myschedule";
    return [
        "Open your calendar directly from here:",
        `- Calendar: ${primaryRoute}`,
        "You can view upcoming and past appointments there.",
    ].join("\n");
}

const TAB_NAVIGATION_TARGETS = [
    {
        route: "/biz/resident",
        pageLabel: "Resident page",
        label: "Resident Face Sheet",
        aliases: ["Face Sheet", "Resident Face Sheet"],
        patterns: [/\bresident face sheet\b/i, /\bface sheet\b/i],
    },
    {
        route: "/biz/resident",
        pageLabel: "Resident page",
        label: "Care Plan",
        aliases: ["Care Plan"],
        patterns: [/\bcare\s*plan\b/i],
    },
    {
        route: "/biz/resident",
        pageLabel: "Resident page",
        label: "Daily Notes",
        aliases: ["Daily Notes"],
        patterns: [/\bdaily\s*notes?\b/i],
    },
    {
        route: "/biz/resident",
        pageLabel: "Resident page",
        label: "Medication & MAR",
        aliases: ["Medication", "MAR", "Medication & MAR", "eMAR"],
        patterns: [/\bmedication\b/i, /\bmar\b/i, /\bemar\b/i],
    },
    {
        route: "/biz/resident",
        pageLabel: "Resident page",
        label: "Vitals",
        aliases: ["Vitals"],
        patterns: [/\bvitals?\b/i],
    },
    {
        route: "/biz/resident",
        pageLabel: "Resident page",
        label: "Alerts",
        aliases: ["Alerts"],
        patterns: [/\balerts?\b/i],
    },
    {
        route: "/biz/resident",
        pageLabel: "Resident page",
        label: "Incident Report",
        aliases: ["Incident Report", "Incident"],
        patterns: [/\bincident(\s*report)?\b/i],
    },
    {
        route: "/biz/resident",
        pageLabel: "Resident page",
        label: "Documents",
        aliases: ["Documents", "Docs", "Doc"],
        patterns: [/\bdocuments?\b/i, /\bdocs?\b/i],
    },
    {
        route: "/biz/resident",
        pageLabel: "Resident page",
        label: "Image",
        aliases: ["Image", "Photo"],
        patterns: [/\bimage\b/i, /\bphoto\b/i],
    },
    {
        route: "/biz/business",
        pageLabel: "Business settings page",
        label: "Subscription & Billing",
        aliases: ["Subscription & Billing", "Subscription and Billing", "Billing"],
        patterns: [/\bsubscription\b/i, /\bbilling\b/i],
    },
    {
        route: "/biz/business",
        pageLabel: "Business settings page",
        label: "Business Basic",
        aliases: ["Business Basic", "Basic"],
        patterns: [/\bbusiness basic\b/i, /\bbasic tab\b/i],
    },
    {
        route: "/biz/business",
        pageLabel: "Business settings page",
        label: "Business Hour",
        aliases: ["Business Hour", "Hours", "Working Hours"],
        patterns: [/\bbusiness hour\b/i, /\bworking hours?\b/i, /\bhours?\b/i],
    },
    {
        route: "/biz/business",
        pageLabel: "Business settings page",
        label: "Images",
        aliases: ["Images", "Business Images", "Photos"],
        patterns: [/\bimages?\b/i, /\bbusiness images?\b/i, /\bphotos?\b/i],
    },
    {
        route: "/biz/business",
        pageLabel: "Business settings page",
        label: "Payment Integration",
        aliases: ["Payment Integration", "Payment"],
        patterns: [/\bpayment integration\b/i, /\bpayment\b/i],
    },
    {
        route: "/biz/business",
        pageLabel: "Business settings page",
        label: "Documents",
        aliases: ["Business Documents", "Business Document", "Documents", "Document", "Docs"],
        patterns: [/\bbusiness documents?\b/i, /\bbusiness docs?\b/i, /\bdocuments?\b/i, /\bdocs?\b/i],
    },
    {
        route: "/biz/staff",
        pageLabel: "Staff page",
        label: "Profile",
        aliases: ["Staff Profile", "Profile"],
        patterns: [/\bstaff profile\b/i, /\bprofile\b/i],
    },
    {
        route: "/biz/staff",
        pageLabel: "Staff page",
        label: "Working Hour",
        aliases: ["Working Hour", "Working Hours", "Hours"],
        patterns: [/\bworking hour\b/i, /\bworking hours?\b/i, /\bhours?\b/i],
    },
    {
        route: "/biz/staff",
        pageLabel: "Staff page",
        label: "Assigned Booking Types",
        aliases: ["Assigned Booking Types", "Assigned Booking Type"],
        patterns: [/\bassigned booking types?\b/i, /\bassign(?:ed)?\s+booking\b/i, /\bbooking types?\b/i],
    },
    {
        route: "/biz/staff",
        pageLabel: "Staff page",
        label: "Integration",
        aliases: ["Integration", "Staff Integration"],
        patterns: [/\bintegration\b/i],
    },
    {
        route: "/biz/staff",
        pageLabel: "Staff page",
        label: "Document",
        aliases: ["Document", "Documents", "Docs", "Staff Document"],
        patterns: [/\bdocument\b/i, /\bdocuments\b/i, /\bdocs?\b/i, /\bstaff document\b/i],
    },
    {
        route: "/biz/staff",
        pageLabel: "Staff page",
        label: "Image",
        aliases: ["Image", "Staff Image", "Photo"],
        patterns: [/\bimage\b/i, /\bstaff image\b/i, /\bphoto\b/i],
    },
];

function startsWithRoute(pathname, route) {
    return String(pathname || "").startsWith(String(route || ""));
}

function normalizeNavigationInput(text = "") {
    const base = String(text || "")
        .toLowerCase()
        .replace(/\bgot to\b/g, "go to")
        .replace(/\bgoto\b/g, "go to");
    return fuzzyNormalizeText(base, FUZZY_NAVIGATION_VOCAB);
}

function detectTabRouteHint(text = "") {
    const value = normalizeNavigationInput(text);
    if (/\bbusiness\b/.test(value)) return "/biz/business";
    if (/\bstaff\b/.test(value)) return "/biz/staff";
    if (/\bresident\b|\bemar\b/.test(value)) return "/biz/resident";
    return "";
}

function hasAnyTabKeyword(text = "") {
    return TAB_NAVIGATION_TARGETS.some((target) =>
        target.patterns.some((pattern) => pattern.test(text))
    );
}

export function isFollowUpNavigationRequest(text = "") {
    const value = String(text || "").toLowerCase().trim();
    if (!value) return false;
    return /^(take me there|go there|open there|bring me there|take me to that|go to that|open that|switch there|take me)$/.test(value);
}

export function resolveTabNavigationTarget(text, pathname = "") {
    const value = String(text || "");
    const normalized = normalizeNavigationInput(value);
    const askedToNavigate = /(take me|go to|open|navigate|bring me|switch to|move to|check|show|see|view|review|let us|let's|let me)/i.test(normalized);
    const mentionsTab = /\btab\b/i.test(value);
    const mentionsPage = /\bpage\b/i.test(value);
    const hasTabKeyword = hasAnyTabKeyword(normalized);
    const routeHint = detectTabRouteHint(normalized);
    const tokenCount = normalized.trim() ? normalized.trim().split(/\s+/).length : 0;
    const looksLikeDirectTabCommand = Boolean(
        hasTabKeyword &&
        routeHint &&
        tokenCount <= 4
    );

    if (
        !mentionsTab &&
        !mentionsPage &&
        !(askedToNavigate && hasTabKeyword) &&
        !looksLikeDirectTabCommand
    ) {
        return null;
    }

    const explicitRouteTargets = TAB_NAVIGATION_TARGETS.filter((target) =>
        routeHint ? startsWithRoute(target.route, routeHint) : false
    );
    const pageScopedTargets = TAB_NAVIGATION_TARGETS.filter((target) =>
        startsWithRoute(pathname, target.route)
    );
    const orderedTargets = explicitRouteTargets.length
        ? [
            ...explicitRouteTargets,
            ...TAB_NAVIGATION_TARGETS.filter((target) => !explicitRouteTargets.includes(target)),
        ]
        : pageScopedTargets.length
        ? [
            ...pageScopedTargets,
            ...TAB_NAVIGATION_TARGETS.filter((target) => !pageScopedTargets.includes(target)),
        ]
        : TAB_NAVIGATION_TARGETS;

    return (
        orderedTargets.find((target) =>
            target.patterns.some((pattern) => pattern.test(normalized))
        ) || null
    );
}

export function matchesAnyLabel(text, labels = []) {
    const normalizedText = String(text || "").replace(/\s+/g, " ").trim().toLowerCase();
    if (!normalizedText) return false;
    return labels.some((label) => {
        const normalizedLabel = String(label || "").replace(/\s+/g, " ").trim().toLowerCase();
        return normalizedLabel && (
            normalizedText === normalizedLabel ||
            normalizedText.includes(normalizedLabel) ||
            normalizedLabel.includes(normalizedText)
        );
    });
}

function isTargetTabActive(target) {
    if (!target || typeof document === "undefined") return false;
    const labels = [target.label, ...(Array.isArray(target.aliases) ? target.aliases : [])];
    const selectedNodes = Array.from(
        document.querySelectorAll(
            ".MuiListItemButton-root.Mui-selected, .MuiTab-root.Mui-selected, [role='tab'][aria-selected='true']"
        )
    ).filter((el) => isVisibleElement(el) && !isElementInsidePlanyBot(el));

    return selectedNodes.some((node) => matchesAnyLabel(node?.textContent || "", labels));
}

export function clickTabByTarget(target) {
    if (!target || typeof document === "undefined") return false;
    if (isTargetTabActive(target)) return true;

    const labels = [target.label, ...(Array.isArray(target.aliases) ? target.aliases : [])];
    const selectors = [
        ".MuiListItemButton-root",
        ".MuiTab-root",
        "[role='tab']",
    ];
    const candidates = Array.from(document.querySelectorAll(selectors.join(",")))
        .filter((el) => isVisibleElement(el) && !isElementInsidePlanyBot(el));
    let clicked = false;

    for (const node of candidates) {
        const nodeText = node?.textContent || "";
        if (!matchesAnyLabel(nodeText, labels)) continue;
        if (typeof node.click === "function") {
            node.click();
            clicked = true;
        }
    }
    if (isTargetTabActive(target)) return true;

    const textNodes = Array.from(
        document.querySelectorAll(".MuiListItemText-primary, .MuiTab-wrapper, .MuiTab-root")
    ).filter((el) => isVisibleElement(el) && !isElementInsidePlanyBot(el));

    for (const node of textNodes) {
        const nodeText = node?.textContent || "";
        if (!matchesAnyLabel(nodeText, labels)) continue;
        const clickable = node.closest(
            ".MuiListItemButton-root, .MuiTab-root, [role='tab']"
        );
        if (clickable && typeof clickable.click === "function") {
            clickable.click();
            clicked = true;
        }
    }
    if (isTargetTabActive(target)) return true;
    return clicked && isTargetTabActive(target);
}

export function resolveNavigationTarget(text, pathname = "") {
    const value = String(text || "");
    const normalized = normalizeNavigationInput(value);
    const isBusinessArea = /^\/biz(?:\/|$)/.test(String(pathname || ""));
    const askedToNavigate = /(take me|go to|got to|goto|open|navigate|bring me|switch to)/i.test(value);
    if (!askedToNavigate) return null;

    if (/\bresident\b|\bresidents\b|\bemar\b/.test(normalized)) {
        return { route: "/biz/resident", label: "Resident page" };
    }
    if (/\bcalendar\b|\bschedule\b/.test(normalized)) {
        return { route: "/biz/calendar", label: "Calendar page" };
    }
    if (/\bpricing\b|\bsubscription\b|\bbilling\b/.test(normalized)) {
        return { route: "/biz/pricing", label: "Pricing page" };
    }
    if (/\bstaff\b/.test(normalized)) {
        return { route: "/biz/staff", label: "Staff page" };
    }
    if (
        /\bbooking page\b|\bbooking\b|\bbooking type\b|\bbooking types\b|\bservices\b/.test(
            normalized
        )
    ) {
        return isBusinessArea
            ? { route: "/biz/bookingtypes", label: "Booking Types page" }
            : { route: "/booking", label: "Booking page" };
    }
    if (/\bprofile\b/.test(normalized)) {
        return { route: "/biz/profile", label: "Profile page" };
    }
    if (/\bmy business\b|\bbusiness page\b|\bbusiness settings\b|\bbusiness\b/.test(normalized)) {
        return { route: "/biz/business", label: "Business settings page" };
    }
    return null;
}

export function buildNavigationReply(target, currentPathname) {
    if (!target?.route) return "";
    if (String(currentPathname || "").startsWith(target.route)) {
        return `You are already on ${target.label}: ${target.route}`;
    }
    return `Opening ${target.label}: ${target.route}`;
}

export function splitMessageLinks(text = "") {
    const source = String(text || "");
    const markdownLinkRe = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)/g;
    const segments = [];
    let lastIndex = 0;
    let markdownMatch;

    const pushBareLinks = (plainText) => {
        if (!plainText) return;
        MESSAGE_LINK_RE.lastIndex = 0;
        let plainLastIndex = 0;
        let linkMatch;

        while ((linkMatch = MESSAGE_LINK_RE.exec(plainText)) !== null) {
            const value = linkMatch[0];
            const startIndex = linkMatch.index;
            if (startIndex > plainLastIndex) {
                segments.push({
                    type: "text",
                    value: plainText.slice(plainLastIndex, startIndex),
                });
            }
            segments.push({ type: "link", value });
            plainLastIndex = startIndex + value.length;
        }

        if (plainLastIndex < plainText.length) {
            segments.push({
                type: "text",
                value: plainText.slice(plainLastIndex),
            });
        }
    };

    while ((markdownMatch = markdownLinkRe.exec(source)) !== null) {
        const [fullMatch, label, href] = markdownMatch;
        const startIndex = markdownMatch.index;
        if (startIndex > lastIndex) {
            pushBareLinks(source.slice(lastIndex, startIndex));
        }
        segments.push({
            type: "link",
            value: href,
            label: toShortText(label, 120),
        });
        lastIndex = startIndex + fullMatch.length;
    }

    if (lastIndex < source.length) {
        pushBareLinks(source.slice(lastIndex));
    }

    return segments.length ? segments : [{ type: "text", value: source }];
}

export function isVisibleElement(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
}

export function isElementInsidePlanyBot(element) {
    if (!element || typeof element.closest !== "function") return false;
    return Boolean(element.closest(BOT_ROOT_SELECTOR));
}

export function getFieldLabel(element) {
    if (!element) return "";
    if (isElementInsidePlanyBot(element)) return "";
    const id = element.getAttribute("id");
    const ariaLabel = element.getAttribute("aria-label");
    const placeholder = element.getAttribute("placeholder");
    const name = element.getAttribute("name");
    const labelledBy = element.getAttribute("aria-labelledby");

    if (id) {
        const explicitLabel = document.querySelector(`label[for="${id}"]`);
        if (explicitLabel?.textContent) return toShortText(explicitLabel.textContent);
    }

    if (labelledBy) {
        const labelledEl = document.getElementById(labelledBy);
        if (labelledEl?.textContent) return toShortText(labelledEl.textContent);
    }

    if (ariaLabel) return toShortText(ariaLabel);
    if (placeholder) return toShortText(placeholder);
    if (name) return toShortText(name);
    return toShortText(element.tagName);
}

export function collectVisibleFormFields(limit = 12) {
    const fields = Array.from(
        document.querySelectorAll("form input, form select, form textarea")
    ).filter((field) => isVisibleElement(field) && !isElementInsidePlanyBot(field));

    const labels = [];
    for (const field of fields) {
        const label = getFieldLabel(field);
        if (!label) continue;
        if (!labels.includes(label)) labels.push(label);
        if (labels.length >= limit) break;
    }
    return labels;
}

export function collectVisibleHeadings(limit = 5) {
    const headingSelectors = "h1, h2, h3, [data-testid='page-title'], [data-page-title]";
    const headings = Array.from(document.querySelectorAll(headingSelectors))
        .filter((el) => isVisibleElement(el) && !isElementInsidePlanyBot(el))
        .map((el) => toShortText(el.textContent))
        .filter(Boolean);
    return [...new Set(headings)].slice(0, limit);
}

export function mergeUniqueList(...lists) {
    const merged = [];
    lists.forEach((list) => {
        (Array.isArray(list) ? list : []).forEach((item) => {
            const text = toShortText(item, 220);
            if (text && !merged.includes(text)) merged.push(text);
        });
    });
    return merged;
}

export function collectSelectedNavigationLabels(limit = 12) {
    const selectors = [
        ".MuiListItemButton-root.Mui-selected .MuiListItemText-primary",
        ".MuiDrawer-root .Mui-selected .MuiListItemText-primary",
        ".MuiTab-root.Mui-selected",
        "[role='tab'][aria-selected='true']",
    ];
    const labels = Array.from(document.querySelectorAll(selectors.join(",")))
        .filter((el) => isVisibleElement(el) && !isElementInsidePlanyBot(el))
        .map((el) => toShortText(el.textContent, 120))
        .filter(Boolean);
    return [...new Set(labels)].slice(0, limit);
}

export function collectVisibleNavigationLabels(limit = 30) {
    const selectors = [
        ".MuiListItemText-primary",
        ".MuiTab-root",
        "[role='tab']",
        ".MuiBreadcrumbs-li",
        ".MuiDrawer-root a",
        "nav a",
        "aside a",
    ];
    const labels = Array.from(document.querySelectorAll(selectors.join(",")))
        .filter((el) => isVisibleElement(el) && !isElementInsidePlanyBot(el))
        .map((el) => toShortText(el.textContent, 140))
        .filter((text) => text && text.length >= 2);
    return [...new Set(labels)].slice(0, limit);
}

export function collectVisibleActionButtons(limit = 10) {
    const selectors = [
        "button",
        "[role='button']",
        "a.MuiButtonBase-root",
        ".MuiButton-root",
    ];
    const labels = Array.from(document.querySelectorAll(selectors.join(",")))
        .filter((el) => isVisibleElement(el) && !isElementInsidePlanyBot(el))
        .map((el) => toShortText(el.textContent, 70))
        .filter((text) => text && text.length >= 2 && !/^[-+]+$/.test(text));
    return [...new Set(labels)].slice(0, limit);
}

export function getRouteContext(pathname) {
    const baseContext = ROUTE_CONTEXTS.find((item) => {
        if (item.matches instanceof RegExp) {
            return item.matches.test(pathname);
        }
        return typeof item.match === "string" && pathname.includes(item.match);
    }) || null;

    const enrichment = ROUTE_ENRICHMENTS.find((item) => item.matches.test(pathname)) || null;
    if (!baseContext && !enrichment) return null;

    return {
        ...(baseContext || {}),
        ...(enrichment || {}),
        tasks: mergeUniqueList(baseContext?.tasks, enrichment?.tasks),
        tabs: mergeUniqueList(baseContext?.tabs, enrichment?.tabs),
        forms: mergeUniqueList(baseContext?.forms, enrichment?.forms),
        workflows: Array.isArray(enrichment?.workflows)
            ? enrichment.workflows
            : (Array.isArray(baseContext?.workflows) ? baseContext.workflows : []),
    };
}

export function isPageScopeQuestion(text) {
    const lower = String(text || "").toLowerCase();
    return /what.*(this|current).*(page|form)|what can i do here|where am i|what is this page|what is this form|about this page|about this form|tell me about (this|current) (page|form)|teach me this (page|form)|explain this (page|form)|help me understand this (page|form)/.test(lower);
}

export function isWorkflowQuestion(text) {
    const lower = String(text || "").toLowerCase();
    return /how|steps|process|where do i|where can i|guide|walk me|configure|set up|setup|create|update|edit|manage|assign|switch|upgrade|downgrade|cancel|integrat|workflow|best way/.test(lower);
}

export function detectActiveTabContext(pathname, selectedLabels = []) {
    if (!selectedLabels.length) return null;
    const tabGroup = ROUTE_TAB_CONTEXT_GROUPS.find((group) => group.path.test(pathname));
    if (!tabGroup) return null;

    for (const selectedLabel of selectedLabels) {
        const matched = tabGroup.tabs.find((tab) => tab.match.test(selectedLabel));
        if (!matched) continue;
        return {
            hint: `Active tab: ${selectedLabel}.`,
            summary: matched.summary,
            tasks: matched.tasks,
            selectedLabel,
        };
    }
    return null;
}

export function findWorkflowMatch(routeContext, questionText) {
    const workflows = Array.isArray(routeContext?.workflows) ? routeContext.workflows : [];
    if (!workflows.length) return null;
    return workflows.find((workflow) => {
        if (workflow?.match instanceof RegExp) return workflow.match.test(questionText);
        return false;
    }) || null;
}

export function buildWorkflowReply(workflow, runtimeContext) {
    const steps = Array.isArray(workflow?.steps) ? workflow.steps : [];
    if (!workflow?.name || !steps.length) return "";
    const lines = [
        `Best workflow for this page: ${workflow.name}.`,
        ...steps.map((step, index) => `${index + 1}. ${step}`),
        runtimeContext.activeTabContext?.summary
            ? `Current tab/section: ${runtimeContext.activeTabContext.summary}.`
            : "",
        runtimeContext.activeFieldLabel
            ? `Current field focus: ${runtimeContext.activeFieldLabel}.`
            : "",
    ].filter(Boolean);
    return lines.join("\n");
}

export function buildScopedPageReply(
    routeContext,
    activeTabContext,
    activeFieldLabel,
    pathname,
    headings = [],
    visibleActionButtons = []
) {
    if (!routeContext?.summary) {
        const headingText = headings.length ? ` Top section: ${headings[0]}.` : "";
        const activeText = activeFieldLabel ? ` Active field: ${activeFieldLabel}.` : "";
        return `You're on ${pathname}.${headingText}${activeText}`.trim();
    }

    const tasks = Array.isArray(routeContext.tasks) ? routeContext.tasks : [];
    const workflowNames = (Array.isArray(routeContext.workflows) ? routeContext.workflows : [])
        .map((wf) => toShortText(wf.name, 80))
        .filter(Boolean);
    const lines = [
        `You're on ${routeContext.summary}.`,
        activeTabContext?.summary ? `Current tab/section: ${activeTabContext.summary}.` : "",
        tasks.length ? `What you can do here: ${tasks.slice(0, 5).join(", ")}.` : "",
        workflowNames.length ? `Common workflows: ${workflowNames.slice(0, 4).join(" | ")}.` : "",
        visibleActionButtons.length
            ? `Visible actions now: ${visibleActionButtons.slice(0, 5).join(" | ")}.`
            : "",
        activeFieldLabel ? `Right now you're focused on: ${activeFieldLabel}.` : "",
    ].filter(Boolean);

    return lines.join(" ");
}

export function buildRuntimeContext() {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return {
            text: "",
            routeContext: null,
            activeFieldLabel: "",
            activeTabContext: null,
            visibleActionButtons: [],
            visibleNavigation: [],
        };
    }

    const pathname = window.location.pathname;
    const routeContext = getRouteContext(pathname);
    const selectedNavigation = collectSelectedNavigationLabels();
    const activeTabContext = detectActiveTabContext(pathname, selectedNavigation);
    const routeHint = activeTabContext?.hint || routeContext?.hint || "";
    const pageTitle = toShortText(document.title, 120);
    const activeElement = document.activeElement;
    const activeFieldLabel = getFieldLabel(activeElement);
    const headings = collectVisibleHeadings();
    const fieldLabels = collectVisibleFormFields();
    const visibleNavigation = collectVisibleNavigationLabels();
    const visibleActionButtons = collectVisibleActionButtons();
    const workflowNames = (Array.isArray(routeContext?.workflows) ? routeContext.workflows : [])
        .map((workflow) => workflow?.name)
        .filter(Boolean);

    const lines = [
        `Current path: ${pathname}`,
        pageTitle ? `Document title: ${pageTitle}` : "",
        routeHint ? `Route hint: ${routeHint}` : "",
        activeFieldLabel ? `Active field: ${activeFieldLabel}` : "",
        selectedNavigation.length ? `Selected navigation: ${selectedNavigation.join(" | ")}` : "",
        headings.length ? `Visible headings: ${headings.join(" | ")}` : "",
        visibleNavigation.length ? `Visible navigation labels: ${visibleNavigation.join(" | ")}` : "",
        visibleActionButtons.length ? `Visible action buttons: ${visibleActionButtons.join(" | ")}` : "",
        fieldLabels.length ? `Visible form fields: ${fieldLabels.join(" | ")}` : "",
        routeContext?.summary ? `Known page summary: ${routeContext.summary}` : "",
        routeContext?.tasks?.length ? `Known page tasks: ${routeContext.tasks.join(" | ")}` : "",
        routeContext?.tabs?.length ? `Known tabs/sections: ${routeContext.tabs.join(" | ")}` : "",
        routeContext?.forms?.length ? `Known forms: ${routeContext.forms.join(" | ")}` : "",
        workflowNames.length ? `Known workflows: ${workflowNames.join(" | ")}` : "",
        activeTabContext?.summary ? `Detected active tab summary: ${activeTabContext.summary}` : "",
        activeTabContext?.tasks?.length
            ? `Detected active tab tasks: ${activeTabContext.tasks.join(" | ")}`
            : "",
    ].filter(Boolean);

    return {
        text: lines.join("\n"),
        routeContext,
        activeTabContext,
        activeFieldLabel,
        pathname,
        headings,
        visibleActionButtons,
        visibleNavigation,
    };
}

